<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import NoticeWelcomeMessage from "./NoticeWelcomeMessage.vue";
import NoticeInternetArchive from "./NoticeInternetArchive.vue";
import NoticeLoading from "./NoticeLoading.vue";

const { currentArticle, contentFiles, isReturningUser, isArticleInverted, showIframe, toggleModals, toggleSidebar, windowWidth } = defineProps({
	currentArticle: Object,
	contentFiles: Object,
	isReturningUser: Boolean,
	isArticleInverted: Boolean,
	showIframe: Boolean,
	toggleModals: Function,
	toggleSidebar: Function,
	windowWidth: Number,
});

// COMPUTED
const shareRoute = computed(() => {
	return 'https://brownjuice.co/study/?article=' + currentArticle?.id
})
const isWelcomeMessage = computed(() => {
	return currentArticle?.id === 'a0' ?? false;
})
const isServiceMessage = computed(() => {
	return currentArticle?.section === 's0' ?? false;
})
const isInternetArchive = computed(() => {
	return currentArticle?.url?.match(/web\.archive\.org/ig) ?? false
})

/* HANDLE SWIPE FROM LEFT EDGE ON MOBILE */
let startX = 0;
let startY = 0;
let endX = 0;
let endY = 0;

function handleTouchStart(e) {
	if (e.touches.length !== 1 || windowWidth >= 768) return;
	const touch = e.touches[0];
	startX = touch.clientX;
	startY = touch.clientY;
}

function handleTouchEnd(e) {
	if (e.changedTouches.length !== 1 || windowWidth >= 768) return;
	const touch = e.changedTouches[0];
	endX = touch.clientX;
	endY = touch.clientY;

	const deltaX = endX - startX;
	const deltaY = endY - startY;

	// Swipe threshold
	const swipeDistance = 50;
	const edgeThreshold = 50; // px from left edge

	console.log(deltaY)
	// Check for horizontal swipe from left edge
	if (
		startX < edgeThreshold &&
		deltaX > swipeDistance &&
		Math.abs(deltaY) < 80 // ignore diagonal swipes
	) {
		toggleSidebar('open');
	}
}

onMounted(() => {
	window.addEventListener('touchstart', handleTouchStart, { passive: true });
	window.addEventListener('touchend', handleTouchEnd, { passive: true });
});

onUnmounted(() => {
	window.removeEventListener('touchstart', handleTouchStart, { passive: true });
	window.removeEventListener('touchend', handleTouchEnd, { passive: true });
});
</script>

<template>
<main id="article-overview">
	<NoticeWelcomeMessage
		v-if="isWelcomeMessage"
		:toggleModals="toggleModals"
	/>
	<article v-else id="summary">
		<slot v-if="!isServiceMessage" name="OverviewControlBar"></slot>

		<h1 class="title" v-if="currentArticle?.title">{{currentArticle?.title}}</h1>
		<p>
			<span class="source" v-if="currentArticle?.source">{{currentArticle?.source}}</span>
			<span v-if="currentArticle?.source && currentArticle?.author">—</span>
			<span class="author" v-if="currentArticle?.author">{{currentArticle?.author}}</span>
		</p>

		<slot v-if="!isServiceMessage && currentArticle?.id" name="ArticleReactions"></slot>
		<slot name="ShareSheet"></slot>

		<section v-if="currentArticle?.tags?.length" class="topic-section" aria-labelledby="topic-heading">
			<ul class="topic-tags">
				<li class="topic-heading">Topics</li>
				<li v-for="tag in currentArticle.tags" :key="tag">{{ tag }}</li>
			</ul>
		</section>

		<NoticeInternetArchive v-if="isInternetArchive" />

		<div v-if="currentArticle?.summary">
			<h2>Summary</h2>
			<p>{{currentArticle?.summary}}</p>
		</div>

		<div v-if="currentArticle?.takeaways"> <!-- hide if no takeaways -->
			<h2>Key Points</h2>
			<div v-for="takeaway in currentArticle?.takeaways" :key="takeaway[0]?.slice(0,17)">
				<h3 class="point">{{takeaway[0]}}</h3>
				<p class="details">{{takeaway[1]}}</p>
			</div>
		</div>
		<NoticeLoading v-if="!contentFiles"></NoticeLoading>
	</article>
</main>
</template>

<style lang="scss" scoped>
#article-overview {
	background: var(--bg2);
	box-sizing: border-box;
	display: block;
	overflow-y: auto;
	flex-grow: 1;
	flex-shrink: 1;

	position: sticky;
		inset: 0 calc(-1 * var(--sidebar-width)) 0 var(--sidebar-width);
		width: var(--main-content-width);
	max-height: 100dvh;

	@media(min-width:768px) {
		inset: 0 0 0 360px;
		max-width: var(--main-content-width);
	}
	@media(min-width:1024px) {
		inset: 0 auto 0 var(--sidebar-width);
		right: auto;
		width: 35vw;
		max-width: 448px;
		&:has(#welcomeMessage) {
			width: var(--main-content-width);
			width: -webkit-fill-available;
			max-width: var(--main-content-width);
			max-width: -webkit-fill-available;
		}
	}
	#app.resizing & { transition: all .3s ease; }
	#app:has(#sidebar.collapsed) & {
		left: 0;
		right: 0;
		width: 100vw;
		max-width: 100vw;
	}
	&:not(:has(articleFrame)) {
		overflow: auto;
	}
}
#summary {
	padding: 8px var(--container-padding) 180px;
	position: relative;

	min-height: fit-content;
	width: -webkit-fill-available;
	max-width: 800px;
	margin-right: auto;
	margin-left: auto;

	@media(min-width:768px) { padding-bottom: 120px; }
	@media(min-width:1024px) { padding-bottom: 80px; }
}
h1.title {
	font-size: 26px;
	font-weight: 200;
	line-height: 1.25em;
	margin: 10px auto 4px;
}
.source {
	font-size: 16px;
	font-weight: bold;
}
.author {
	font-size: 16px;
	margin-top: 0;
}

h2 {
	font-size: 20px;
	font-weight: bold;
	margin-bottom: .2em;
}
p {
	font-size: 16px;
	font-weight: normal;
	line-height: 1.4;
	margin-top: 0;
}
ul {
	margin-top: 0;
	padding-left: 1em;
}
.topic-section { margin:16px 0; }
.topic-tags {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin: 0;
	padding: 0;
	list-style: none;
}
.topic-tags .topic-heading {
	color: var(--text2);
	background: transparent;
	border: transparent;
	font-size: 14px;
	font-weight: 600;
	margin: 0;
	padding-inline:0;
	float:left;
}
.topic-tags li {
	box-sizing: border-box;
	max-width: 80%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	border: 1px solid var(--border);
	border-radius: 999px;
	padding: 4px 9px;
	color: var(--text2);
	font-size: 13px;
	line-height: 1.25;
}
h3 {
	font-size: 16px;
	font-weight: bold;
	margin: 0 auto .25em;
}
</style>
